"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useEffect, type JSX } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ObjectSerializer";
import { TabToggleWithOverflow } from "@/components/common/TabToggleWithOverflow";
import WaveHeader, { WaveHeaderPinnedSide } from "@/components/waves/header/WaveHeader";
import { WaveWinnersSmall } from "@/components/waves/winners/WaveWinnersSmall";
import BrainRightSidebarContent from "./BrainRightSidebarContent";
import BrainRightSidebarFollowers from "./BrainRightSidebarFollowers";
import { Mode, SidebarTab } from "./BrainRightSidebar";
import { WaveSmallLeaderboard } from "@/components/waves/small-leaderboard/WaveSmallLeaderboard";
import { WaveLeaderboardRightSidebarVoters } from "@/components/waves/leaderboard/sidebar/WaveLeaderboardRightSidebarVoters";
import { WaveLeaderboardRightSidebarActivityLogs } from "@/components/waves/leaderboard/sidebar/WaveLeaderboardRightSidebarActivityLogs";
import { useWaveTimers } from "@/hooks/useWaveTimers";
interface WaveContentProps {
  readonly wave: ApiWave;
  readonly mode: Mode;
  readonly setMode: (mode: Mode) => void;
  readonly activeTab: SidebarTab;
  readonly setActiveTab: (tab: SidebarTab) => void;
}
interface TabOption {
  key: SidebarTab;
  label: string;
}
export const WaveContent: React.FC<WaveContentProps> = t0 => {
  const $ = _c(62);
  const {
    wave,
    mode,
    setMode,
    activeTab,
    setActiveTab
  } = t0;
  let t1;
  if ($[0] !== mode || $[1] !== setMode) {
    t1 = () => setMode(mode === Mode.FOLLOWERS ? Mode.CONTENT : Mode.FOLLOWERS);
    $[0] = mode;
    $[1] = setMode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onFollowersClick = t1;
  const isRankWave = wave.wave.type === ApiWaveType.Rank;
  const {
    voting: t2,
    decisions: t3
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t2;
  const {
    firstDecisionDone
  } = t3;
  let t4;
  let t5;
  if ($[3] !== activeTab || $[4] !== firstDecisionDone || $[5] !== isCompleted || $[6] !== setActiveTab) {
    t4 = () => {
      const isLeaderboardAndVotingEnded = activeTab === SidebarTab.LEADERBOARD && isCompleted;
      const isWinnersAndFirstDecisionNotPassed = activeTab === SidebarTab.WINNERS && !firstDecisionDone;
      if (isLeaderboardAndVotingEnded || isWinnersAndFirstDecisionNotPassed) {
        setActiveTab(SidebarTab.ABOUT);
      }
    };
    t5 = [isCompleted, firstDecisionDone, activeTab, setActiveTab];
    $[3] = activeTab;
    $[4] = firstDecisionDone;
    $[5] = isCompleted;
    $[6] = setActiveTab;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      key: SidebarTab.ABOUT,
      label: "About"
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let tabs;
  if ($[10] !== firstDecisionDone || $[11] !== isCompleted) {
    tabs = [t6];
    if (!isCompleted) {
      let t7;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = {
          key: SidebarTab.LEADERBOARD,
          label: "Leaderboard"
        };
        $[13] = t7;
      } else {
        t7 = $[13];
      }
      tabs.push(t7);
    }
    if (firstDecisionDone) {
      let t7;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = {
          key: SidebarTab.WINNERS,
          label: "Winners"
        };
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      tabs.push(t7);
    }
    let t7;
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        key: SidebarTab.TOP_VOTERS,
        label: "Voters"
      };
      t8 = {
        key: SidebarTab.ACTIVITY_LOG,
        label: "Activity"
      };
      $[15] = t7;
      $[16] = t8;
    } else {
      t7 = $[15];
      t8 = $[16];
    }
    tabs.push(t7, t8);
    $[10] = firstDecisionDone;
    $[11] = isCompleted;
    $[12] = tabs;
  } else {
    tabs = $[12];
  }
  const options = tabs;
  let t7;
  if ($[17] !== onFollowersClick || $[18] !== wave) {
    t7 = <WaveHeader wave={wave} onFollowersClick={onFollowersClick} useRing={false} useRounded={false} pinnedSide={WaveHeaderPinnedSide.LEFT} />;
    $[17] = onFollowersClick;
    $[18] = wave;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== mode || $[21] !== setMode || $[22] !== wave) {
    t8 = mode === Mode.CONTENT ? <BrainRightSidebarContent wave={wave} /> : <BrainRightSidebarFollowers wave={wave} closeFollowers={() => setMode(Mode.CONTENT)} />;
    $[20] = mode;
    $[21] = setMode;
    $[22] = wave;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t7 || $[25] !== t8) {
    t9 = <div className="tw-h-full tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-700">{t7}{t8}</div>;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== wave) {
    t10 = <div><WaveSmallLeaderboard wave={wave} /></div>;
    $[27] = wave;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== wave) {
    t11 = <div><WaveWinnersSmall wave={wave} /></div>;
    $[29] = wave;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== wave) {
    t12 = <div className="tw-p-4"><WaveLeaderboardRightSidebarVoters wave={wave} /></div>;
    $[31] = wave;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== wave) {
    t13 = <div className="tw-p-4"><WaveLeaderboardRightSidebarActivityLogs wave={wave} /></div>;
    $[33] = wave;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t9) {
    t14 = {
      [SidebarTab.ABOUT]: t9,
      [SidebarTab.LEADERBOARD]: t10,
      [SidebarTab.WINNERS]: t11,
      [SidebarTab.TOP_VOTERS]: t12,
      [SidebarTab.ACTIVITY_LOG]: t13
    };
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t9;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const rankWaveComponents = t14;
  if (!isRankWave) {
    let t15;
    if ($[41] !== onFollowersClick || $[42] !== wave) {
      t15 = <WaveHeader wave={wave} onFollowersClick={onFollowersClick} useRing={false} useRounded={false} pinnedSide={WaveHeaderPinnedSide.LEFT} />;
      $[41] = onFollowersClick;
      $[42] = wave;
      $[43] = t15;
    } else {
      t15 = $[43];
    }
    let t16;
    if ($[44] !== mode || $[45] !== setMode || $[46] !== wave) {
      t16 = mode === Mode.CONTENT ? <BrainRightSidebarContent wave={wave} /> : <BrainRightSidebarFollowers wave={wave} closeFollowers={() => setMode(Mode.CONTENT)} />;
      $[44] = mode;
      $[45] = setMode;
      $[46] = wave;
      $[47] = t16;
    } else {
      t16 = $[47];
    }
    let t17;
    if ($[48] !== t15 || $[49] !== t16) {
      t17 = <div className="tw-h-full tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-700">{t15}{t16}</div>;
      $[48] = t15;
      $[49] = t16;
      $[50] = t17;
    } else {
      t17 = $[50];
    }
    return t17;
  }
  let t15;
  if ($[51] !== setActiveTab) {
    t15 = key => setActiveTab(key as SidebarTab);
    $[51] = setActiveTab;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== activeTab || $[54] !== options || $[55] !== t15) {
    t16 = <div className="tw-pb-px tw-pl-2.5"><TabToggleWithOverflow options={options} activeKey={activeTab} onSelect={t15} maxVisibleTabs={3} /></div>;
    $[53] = activeTab;
    $[54] = options;
    $[55] = t15;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  const t17 = rankWaveComponents[activeTab];
  let t18;
  if ($[57] !== t17) {
    t18 = <div>{t17}</div>;
    $[57] = t17;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== t16 || $[60] !== t18) {
    t19 = <>{t16}{t18}</>;
    $[59] = t16;
    $[60] = t18;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  return t19;
};